import { Navigate, Outlet, useLocation } from "react-router-dom";

import { useFeatureFlags } from "@/query/hooks/useFeatureFlags";

/**
 * Route guard for `/recap`. Transport tenants (the `transportMode` flag on) are
 * sent to the transport recap sibling; sales tenants keep the sales recap.
 *
 * The redirect MUST be flag-gated. Its previous form redirected every `/recap`
 * path unconditionally, which made the sales recap unreachable for every sales
 * tenant (`transportMode` off) — and broke the recap E2E suite, whose tenants
 * are sales tenants. The tenant's default landing already honours the flag
 * (`resolveLandingPath`); this guard additionally catches deep links
 * (`/recap/:id` from a notification or bookmark) so a transport tenant never
 * lands on the retired sales surface, while a sales tenant is never bounced off
 * it.
 *
 * `/recap` → `/recap_transport`, `/recap/mobile` → `/recap_transport`,
 * `/recap/:id` → `/recap_transport/:id`; the query string and hash are
 * preserved.
 */
export function RecapTransportGate() {
  const { data: flags, isPending } = useFeatureFlags();
  const location = useLocation();

  // Withhold the surface until the flag is known. Rendering the sales Outlet
  // while `transportMode` is still loading (cold start: empty cache, no
  // IndexedDB hydration yet) would flash the sales recap — and fire its
  // sales-data requests — for a transport tenant deep-linking to `/recap/:id`
  // before the redirect runs. On a flag-fetch error `isPending` is false and
  // the tenant safely falls through to the sales recap.
  if (isPending) return null;

  if (flags?.transportMode === true) {
    const target =
      location.pathname.replace(/^\/recap(\/mobile)?/, "/recap_transport") +
      location.search +
      location.hash;
    return <Navigate to={target} replace />;
  }

  return <Outlet />;
}
